import { IframeQueryParameters } from '@wix/wix-chatbot-common/browser';
import React, { FC, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';

import { Chatbot } from '../../../chatbot';
import { Loader } from '../../../common/components/Loader';
import { selectChatbotDataLoading } from '../../../common/selectors/chat';
import { ACTIONS } from '../../../constants';
import {
  biHelpCenter,
  locationToSolutionInfo,
  PresentedState,
} from '../../../util/bi';
import { emitter } from '../../../util/emitter';
import { ChatbotToggle } from '../ChatbotToggle';
import s from './styles.scss';

export const ChatbotSlider: FC = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const isOpen =
    searchParams.get(IframeQueryParameters.isChatbotOpen) !== 'false';
  const { pathname, search } = useLocation();
  const chatbotSliderRef = useRef<HTMLDivElement>(null);
  const chatbotDataLoading = useSelector(selectChatbotDataLoading);

  const onChatbotTransformEnded = useCallback(
    (event: TransitionEvent) => {
      if (event.propertyName !== 'transform') {
        return;
      }

      // As this function is called in onToggle, isOpen value is treated as isCollapsed value
      const isCollapsed = isOpen;

      if (isCollapsed) {
        const solution = locationToSolutionInfo(
          pathname,
          search,
          biHelpCenter.searchArticlesList!,
        );
        biHelpCenter.logPresentedContent(solution, PresentedState.Chatbot);
      }

      chatbotSliderRef.current?.removeEventListener(
        'transitionend',
        onChatbotTransformEnded,
      );
    },
    [isOpen, pathname, search],
  );

  const handleChatbotCollapse = useCallback(() => {
    chatbotSliderRef.current?.addEventListener(
      'transitionend',
      onChatbotTransformEnded,
    );
  }, [onChatbotTransformEnded]);

  const onToggle = useCallback(
    (skipBiLog = false) => {
      if (!skipBiLog) {
        biHelpCenter.logChatbotSliderInteraction(isOpen, pathname, search);
      }
      handleChatbotCollapse();
      // We treat isOpen as isCollapsed value
      searchParams.set(IframeQueryParameters.isChatbotOpen, String(!isOpen));
      setSearchParams(searchParams, { replace: true });
      if (isOpen) {
        dispatch({
          type: ACTIONS.INPUTS_FOCUS_SEARCH,
          search: true,
        });
      }
    },
    [
      handleChatbotCollapse,
      searchParams,
      isOpen,
      setSearchParams,
      pathname,
      search,
      dispatch,
    ],
  );

  const hide = useCallback(() => isOpen && onToggle(true), [isOpen, onToggle]);
  const show = useCallback(() => !isOpen && onToggle(true), [isOpen, onToggle]);

  useEffect(() => {
    emitter.on('toggleChatbotVisible', (visible) => {
      if (visible) {
        show();
      } else {
        hide();
      }
    });
    return () => emitter.remove('toggleChatbotVisible');
  }, [hide, show]);

  return (
    <div
      ref={chatbotSliderRef}
      style={{ transform: isOpen ? 'translateY(60px)' : 'translateY(100%)' }}
      className={s.chatbotSlider}
      data-hook="chatbot-slider"
    >
      <ChatbotToggle expanded={isOpen} onToggle={() => onToggle()} />
      {chatbotDataLoading ? (
        <Loader
          height="100%"
          width="100%"
          verticalAlign="middle"
          backgroundColor="D70"
        />
      ) : (
        <Chatbot store={store} isOpen={isOpen} />
      )}
    </div>
  );
};
